import React, { useMemo, useRef, useEffect, useState, Suspense } from "react";
import { Constellation, EnergyOrb, VoiceWave, LightIris, NeuronBrain, LOOKS } from "./NexusLooks";
import { Canvas, useFrame, useThree, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";

const PUB = process.env.PUBLIC_URL || "";

const NOISE_GLSL = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0); const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy)); vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz); vec3 l=1.0-g; vec3 i1=min(g.xyz,l.zxy); vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx; vec3 x2=x0-i2+C.yyy; vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857; vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z); vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy; vec4 y=y_*ns.x+ns.yyyy; vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy); vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0; vec4 s1=floor(b1)*2.0+1.0; vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy; vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x); vec3 p1=vec3(a0.zw,h.y); vec3 p2=vec3(a1.xy,h.z); vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0); m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}`;

/* voice level (0..1) with gentle idle breathing */
function useLevel(levelRef, state, dtScale = 9) {
  const smooth = useRef(0);
  return (t, dt) => {
    const raw = levelRef?.current || 0;
    const idle = 0.05 + Math.abs(Math.sin(t * 1.1)) * 0.06;
    const lvl = Math.max(raw, state === "idle" ? idle : idle * 0.55);
    smooth.current += (lvl - smooth.current) * Math.min(1, dt * dtScale);
    return smooth.current;
  };
}

/* ============ OPTION C — liquid-metal chrome orb with energy veins ============ */
const METAL_VERT = `
uniform float uTime; uniform float uAmp;
varying vec3 vN; varying vec3 vView; varying float vNoise;
${NOISE_GLSL}
void main(){
  vec3 nrm = normalize(position);
  float n  = snoise(nrm*1.7 + uTime*0.3);
  float n2 = snoise(nrm*3.6 - uTime*0.5);
  float disp = n*(0.14 + uAmp*0.55) + n2*(0.05 + uAmp*0.15);
  vNoise = n;
  vec3 p = position + normal*disp;
  vec4 wp = modelMatrix * vec4(p,1.0);
  vN = normalize(mat3(modelMatrix)*normal);
  vView = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;
const METAL_FRAG = `
uniform vec3 uColor; uniform float uTime; uniform float uAmp;
varying vec3 vN; varying vec3 vView; varying float vNoise;
void main(){
  float fres = pow(1.0 - max(dot(vN, vView), 0.0), 2.6);
  vec3 r = reflect(-vView, vN);
  float band = 0.5 + 0.5*sin(r.y*5.0 + r.x*3.0 + 1.0);
  vec3 chrome = mix(vec3(0.008,0.02,0.035), vec3(0.16,0.28,0.4), band*band);
  float f = fract(vNoise*4.5 + uTime*0.12);
  float vein = pow(1.0 - abs(f-0.5)*2.0, 10.0);
  vec3 col = chrome + uColor*fres*0.55 + uColor*vein*(0.32 + uAmp*1.3);
  gl_FragColor = vec4(col, 1.0);
}`;

function LiquidMetal({ colorHex, state, levelRef }) {
  const group = useRef();
  const core = useRef();
  const level = useLevel(levelRef, state);
  const { nodeGeo, lineGeo } = useMemo(() => {
    const N = 130;
    const pts = [];
    const inc = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const phi = i * inc;
      pts.push(new THREE.Vector3(Math.cos(phi) * r, y, Math.sin(phi) * r).multiplyScalar(1.65));
    }
    const nodePos = new Float32Array(N * 3);
    pts.forEach((p, i) => { nodePos[i * 3] = p.x; nodePos[i * 3 + 1] = p.y; nodePos[i * 3 + 2] = p.z; });
    const ng = new THREE.BufferGeometry();
    ng.setAttribute("position", new THREE.BufferAttribute(nodePos, 3));
    const linePos = [];
    for (let i = 0; i < N; i++) {
      const d = [];
      for (let j = 0; j < N; j++) if (i !== j) d.push([pts[i].distanceTo(pts[j]), j]);
      d.sort((a, b) => a[0] - b[0]);
      for (let k = 0; k < 3; k++) { const j = d[k][1]; if (j > i) linePos.push(pts[i].x, pts[i].y, pts[i].z, pts[j].x, pts[j].y, pts[j].z); }
    }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute("position", new THREE.BufferAttribute(new Float32Array(linePos), 3));
    return { nodeGeo: ng, lineGeo: lg };
  }, []);
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime;
    const amp = level(t, dt);
    if (group.current) {
      group.current.rotation.y += dt * (state === "thinking" ? 0.45 : 0.16);
      group.current.rotation.x = Math.sin(t * 0.15) * 0.12;
      group.current.scale.setScalar(1 + amp * 0.14);
    }
    if (core.current) core.current.scale.setScalar(0.85 + amp * 1.4 + Math.sin(t * 2.2) * 0.06);
  });
  return (
    <group ref={group}>
      <lineSegments geometry={lineGeo}>
        <lineBasicMaterial color={colorHex} transparent opacity={0.32} depthWrite={false} blending={THREE.AdditiveBlending} />
      </lineSegments>
      <points geometry={nodeGeo}>
        <pointsMaterial color={colorHex} size={0.075} sizeAttenuation transparent opacity={0.95} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
      <mesh ref={core}>
        <sphereGeometry args={[0.16, 24, 24]} />
        <meshBasicMaterial color={"#EAFEFF"} toneMapped={false} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.5, 24, 24]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.12} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

/* ============ OPTION D — true 3D holographic head (particles + fresnel shell, lip-sync jaw) ============ */
const LIP_GLSL = `
uniform float uAmp; uniform float uTime;
vec3 lip(vec3 p){
  float y0 = -0.12;
  float front = smoothstep(0.9, 1.7, p.z);
  float side = 1.0 - smoothstep(0.55, 1.35, abs(p.x));
  float below = smoothstep(y0, y0 - 0.22, p.y) * (1.0 - smoothstep(y0 - 0.95, y0 - 1.5, p.y));
  float above = smoothstep(y0, y0 + 0.1, p.y) * (1.0 - smoothstep(y0 + 0.1, y0 + 0.38, p.y));
  float j = uAmp * front * side;
  p.y -= j * below * 0.5; p.z -= j * below * 0.14; p.y += j * above * 0.09;
  return p;
}`;
const SHELL_VERT = `
${LIP_GLSL}
varying vec3 vN; varying vec3 vView; varying vec3 vLocal;
void main(){
  vec3 p = lip(position); vLocal = p;
  vec4 wp = modelMatrix * vec4(p, 1.0);
  vN = normalize(mat3(modelMatrix) * normal);
  vView = normalize(cameraPosition - wp.xyz);
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;
const SHELL_FRAG = `
uniform vec3 uColor; uniform float uTime; uniform float uAmp; uniform float uEmote;
varying vec3 vN; varying vec3 vView; varying vec3 vLocal;
void main(){
  float fres = pow(1.0 - max(dot(vN, vView), 0.0), 2.4);
  float bands = pow(0.5 + 0.5 * sin(vLocal.y * 14.0 - uTime * 1.6), 24.0);
  float sweep = smoothstep(0.25, 0.0, abs(fract(vLocal.y * 0.12 - uTime * 0.18) - 0.5) - 0.22);
  float a = (fres * 0.9 + bands * 0.22 + sweep * 0.35) * uEmote;
  vec3 col = uColor * (0.15 + fres * 0.9 + bands * 0.45 + sweep * 0.5) * (1.0 + uAmp * 0.5);
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0) * 0.45);
}`;
const DOTS_VERT = `
${LIP_GLSL}
attribute float aSeed; varying float vTw; varying float vFront;
uniform float uSize;
void main(){
  vec3 p = lip(position);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  vTw = 0.55 + 0.45 * sin(uTime * 2.5 + aSeed * 40.0);
  vFront = smoothstep(-2.5, 2.5, p.z);
  gl_PointSize = uSize * (0.6 + aSeed * 0.8) * (8.0 / -mv.z);
  gl_Position = projectionMatrix * mv;
}`;
const DOTS_FRAG = `
uniform vec3 uColor; uniform float uEmote; uniform float uAmp;
varying float vTw; varying float vFront;
void main(){
  vec2 c = gl_PointCoord - 0.5; float d = length(c);
  if (d > 0.5) discard;
  float g = smoothstep(0.5, 0.0, d);
  gl_FragColor = vec4(mix(uColor, vec3(0.85, 1.0, 1.0), g * 0.25) * (0.35 + vFront * 0.55) * (1.0 + uAmp * 0.6), g * vTw * uEmote * 0.42);
}`;

function HoloHead({ colorHex, state, levelRef, gazeRef, mood = "neutral" }) {
  const group = useRef();
  const gltf = useLoader(GLTFLoader, `${PUB}/nexus/head.glb`);
  const { geo, dots } = useMemo(() => {
    let mesh = null;
    gltf.scene.traverse((o) => { if (o.isMesh && !mesh) mesh = o; });
    const g = mesh.geometry;
    const sampler = new MeshSurfaceSampler(new THREE.Mesh(g)).build();
    const N = 16000, pos = new Float32Array(N * 3), seed = new Float32Array(N), v = new THREE.Vector3();
    for (let i = 0; i < N; i++) { sampler.sample(v); pos[i * 3] = v.x; pos[i * 3 + 1] = v.y; pos[i * 3 + 2] = v.z; seed[i] = Math.random(); }
    const dg = new THREE.BufferGeometry();
    dg.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    dg.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    return { geo: g, dots: dg };
  }, [gltf]);
  const uni = useMemo(() => ({
    uTime: { value: 0 }, uAmp: { value: 0 }, uEmote: { value: 1 }, uSize: { value: 1.5 },
    uColor: { value: new THREE.Color(colorHex) },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const target = useMemo(() => new THREE.Color(colorHex), [colorHex]);
  const level = useLevel(levelRef, state, 16);
  const look = useRef({ yaw: 0, pitch: 0, next: 2 });
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime;
    const raw = level(t, dt);
    const amp = state === "speaking" ? Math.min(1, raw * 1.6) : raw * 0.15;
    uni.uTime.value = t; uni.uAmp.value = amp;
    uni.uColor.value.lerp(target, Math.min(1, dt * 3));
    const happy = mood === "good", worried = mood === "danger";
    uni.uEmote.value = (1.0 + Math.sin(t * (happy ? 1.8 : worried ? 0.7 : 1.15)) * 0.06) * (happy ? 1.15 : worried ? 0.85 : 1);
    if (!group.current) return;
    const g = gazeRef && gazeRef.current;
    const tracking = g && g.present && Date.now() - g.at < 4000;
    const L = look.current;
    L.next -= dt;
    if (L.next <= 0) {
      L.next = state === "speaking" ? 2.5 + Math.random() * 2 : 1.8 + Math.random() * 3.2;
      L.yaw = state === "speaking" ? (Math.random() - 0.5) * 0.35 : (Math.random() - 0.5) * 1.1;
      L.pitch = (Math.random() - 0.5) * 0.25;
    }
    const ty = tracking ? g.x * 0.6 : L.yaw;
    const tx = (tracking ? g.y * 0.25 : L.pitch) + (worried ? 0.12 : happy ? -0.06 : 0)
      + (state === "speaking" ? Math.sin(t * 5.5) * amp * 0.04 : 0);
    const k = Math.min(1, dt * (tracking ? 4 : 1.8));
    group.current.rotation.y += (ty - group.current.rotation.y) * k;
    group.current.rotation.x += (tx - group.current.rotation.x) * k;
    group.current.rotation.z += ((happy ? Math.sin(t * 0.8) * 0.04 : worried ? -0.05 : 0) - group.current.rotation.z) * k;
    group.current.position.y = -0.35 + Math.sin(t * 0.6) * 0.04;
  });
  return (
    <group ref={group} scale={0.5}>
      <mesh geometry={geo}>
        <shaderMaterial vertexShader={SHELL_VERT} fragmentShader={SHELL_FRAG} uniforms={uni}
          transparent depthWrite={false} blending={THREE.AdditiveBlending} side={THREE.FrontSide} toneMapped={false} />
      </mesh>
      <points geometry={dots}>
        <shaderMaterial vertexShader={DOTS_VERT} fragmentShader={DOTS_FRAG} uniforms={uni}
          transparent depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </points>
    </group>
  );
}

/* ============ shared: orbital rings + starfield + camera drift + bloom ============ */
function Rings({ colorHex, state }) {
  const a = useRef(), b = useRef();
  useFrame((st, dt) => {
    if (a.current) a.current.rotation.z += dt * 0.3;
    if (b.current) { b.current.rotation.x += dt * 0.4; b.current.rotation.y += dt * (state === "thinking" ? 0.9 : 0.18); }
  });
  return (
    <>
      <mesh ref={a} rotation={[Math.PI / 2.2, 0, 0]}>
        <torusGeometry args={[2.7, 0.006, 16, 220]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.45} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh ref={b} rotation={[0, Math.PI / 3, Math.PI / 4]}>
        <torusGeometry args={[3.2, 0.005, 16, 220]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.3} blending={THREE.AdditiveBlending} />
      </mesh>
    </>
  );
}

function Stars({ colorHex }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const N = 520, pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const r = 4 + Math.random() * 6, th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
      pos[i * 3 + 1] = r * Math.sin(ph) * Math.sin(th);
      pos[i * 3 + 2] = r * Math.cos(ph) - 2;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);
  useFrame((st, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.03; });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial color={colorHex} size={0.03} transparent opacity={0.5} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

function Rig() {
  const { camera } = useThree();
  useFrame((st) => {
    const t = st.clock.elapsedTime;
    camera.position.x += (Math.sin(t * 0.14) * 0.5 - camera.position.x) * 0.02;
    camera.position.y += (Math.cos(t * 0.11) * 0.3 - camera.position.y) * 0.02;
    camera.lookAt(0, 0, 0);
  });
  return null;
}

function Effects() {
  const { gl, scene, camera, size } = useThree();
  const composer = useMemo(() => {
    const c = new EffectComposer(gl);
    c.addPass(new RenderPass(scene, camera));
    c.addPass(new UnrealBloomPass(new THREE.Vector2(size.width, size.height), 0.55, 0.5, 0.4));
    c.addPass(new OutputPass());
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl, scene, camera]);
  useEffect(() => { composer.setSize(size.width, size.height); }, [composer, size]);
  useFrame(() => { composer.render(); }, 1);
  return null;
}

function useLook(variant) {
  const [i, setI] = useState(0);
  const cyc = String(variant || "").startsWith("cycle");
  const mins = Math.max(1, Number(String(variant).split(":")[1]) || 10);
  useEffect(() => {
    if (!cyc) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % LOOKS.length), mins * 60000);
    return () => clearInterval(t);
  }, [cyc, mins]);
  return cyc ? LOOKS[i][0] : (LOOKS.some(([id]) => id === variant) ? variant : "neuron");
}

export default function NexusFace3D({ colorHex = "#00F0FF", state = "idle", levelRef, variant = "constellation", gazeRef, mood }) {
  const look = useLook(variant);
  const p = { colorHex, state, levelRef, gazeRef };
  return (
    <Canvas
      data-testid="nexus-3d-face-canvas"
      dpr={[1, 2]}
      camera={{ position: [0, 0, 6.5], fov: 45 }}
      gl={{ antialias: true }}
    >
      <color attach="background" args={["#030712"]} />
      <ambientLight intensity={0.5} />
      <Rig />
      <Stars colorHex={colorHex} />
      {!["wave", "iris", "neuron"].includes(look) && <Rings colorHex={colorHex} state={state} />}
      <Suspense fallback={null}>
        {look === "brain" ? <LiquidMetal {...p} />
          : look === "face" ? <HoloHead {...p} mood={mood} />
          : look === "orb" ? <EnergyOrb {...p} />
          : look === "wave" ? <VoiceWave {...p} />
          : look === "iris" ? <LightIris {...p} />
          : look === "neuron" ? <NeuronBrain {...p} colorHex="#3B82F6" />
          : <Constellation {...p} />}
      </Suspense>
      <Effects />
    </Canvas>
  );
}
